import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Rows4 } from "lucide-react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useFactory } from "../../store";
import type { InspectionMode } from "../../types";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

const MODES: Record<InspectionMode, { label: string; detail: string }> = {
  individual: {
    label: "Individual reviewers",
    detail: "Each reviewer checks one category, and they run in parallel on every pull-request head.",
  },
  combined: {
    label: "Meta Reviewer",
    detail: "One reviewer checks every category in a single pass and tags each finding with its category.",
  },
};

export function InspectionCard() {
  const { layout } = useFactory();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: api.setInspectionMode,
    onSuccess: ({ mode }) => {
      void queryClient.invalidateQueries({ queryKey: ["layout"] });
      toast.success(`Inspection: ${MODES[mode].label}`);
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Request failed"),
  });
  const inspection = layout?.inspection;
  if (!inspection) return null;

  const mode = save.isPending && save.variables ? save.variables : inspection.mode;
  const names = new Map(layout.sections.flatMap((section) => section.agents).map((a) => [a.id, a.name]));
  const stations = mode === "combined" ? [inspection.combined] : inspection.reviewers;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Inspection</CardTitle>
        <CardDescription>Who reviews each pull request before the Verifier</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Label>Reviewers</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={mode}
            disabled={save.isPending}
            onValueChange={(value) => value && value !== mode && save.mutate(value as InspectionMode)}
          >
            <ToggleGroupItem value="individual" aria-label={MODES.individual.label}>
              <Rows4 /> {MODES.individual.label}
            </ToggleGroupItem>
            <ToggleGroupItem value="combined" aria-label={MODES.combined.label}>
              <ListChecks /> {MODES.combined.label}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
          <p className="text-sm">{MODES[mode].detail}</p>
          <div className="flex flex-wrap gap-1.5">
            {stations.map((id) => (
              <Badge key={id} variant="secondary">
                {names.get(id) ?? id}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Applies from the next inspection batch. A batch that is already running finishes as it started.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
